import { produce } from 'immer'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useNavigate, useParams } from 'react-router-dom'

import { Alert, Button, ButtonGroup, Dialog, DialogBody, Divider, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import useCurrentUserUUID from '../../aws/useCurrentUserUUID'
import card from '../../domain/api/card'
import me from '../../domain/api/me'
import CardDetail from '../cardDetail/CardDetail'
import ApiError from '../common/apiError/ApiError'
import Empty from '../common/empty/Empty'
import Mount from '../common/mount/Mount'
import { useRootOutletContext } from '../root/Root'
import styles from './Card.module.scss'
import CardSkeleton from './CardSkeleton'

export default ({
    signInLink,
    signUpLink,
    navigatePostRemovingFromMyCard,
}: {
    signInLink: (cardId: string) => To,
    signUpLink: (cardId: string) => To,
    navigatePostRemovingFromMyCard: (cardId: string) => To,
}) => {
    const { cardId } = useParams()
    const cardResponse = card.useFetchSWR(cardId)
    const { data, mutate } = cardResponse
    const { trigger: insertCard, isMutating: isInsertingCard, } = me.cards.useInsertSWR()
    const { trigger: rejectCard, isMutating: isRejectingCard, } = me.cards.useRejectSWR(data?.id)
    const { t } = useTranslation()
    const { toaster } = useRootOutletContext()
    const navigate = useNavigate()
    const auth = useAuth()

    const [state, setState] = useState({
        isSignInDialogOpen: false,
        isRemoveMyCardDialogOpen: false,
    })

    const cognitoUUID = useCurrentUserUUID()

    const addToMyCard = useCallback(async () => {
        if (!auth.isAuthenticated) {
            setState(produce(draft => {
                draft.isSignInDialogOpen = true
            }))
            return
        }
        const result = await insertCard({ card_id: data?.id ?? '' })
        if (result.status === 201) {
            mutate(produce(draft => {
                draft.cognito_uuid = cognitoUUID
            }))
            toaster?.show({ message: t('toast.addedToMyCard.message'), icon: IconNames.Endorsed, intent: Intent.SUCCESS })
        } else {
            toaster?.show({ message: t('toast.failedToAddToMyCard.message'), icon: IconNames.Error, intent: Intent.DANGER })
        }
    }, [insertCard, toaster, mutate, t, auth, cognitoUUID, data])

    const removeMyCardConfirmed = useCallback(async () => {
        const result = await rejectCard()
        setState(produce(draft => {
            draft.isRemoveMyCardDialogOpen = false
        }))

        if (result.status !== 204)
            return toaster?.show({ message: t('toast.failedToRemoveFromMyCard.message'), icon: IconNames.Error, intent: Intent.DANGER })

        toaster?.show({ message: t('toast.removedFromMyCard.message'), icon: IconNames.Remove, intent: Intent.SUCCESS })
        mutate(produce(draft => {
            draft.cognito_uuid = undefined
        }))
        navigate(navigatePostRemovingFromMyCard(data?.id ?? ''))
    }, [rejectCard, toaster, mutate, navigate, data, t, navigatePostRemovingFromMyCard])

    const linkedUser = useCallback((cardUserUuid?: string) => !cardUserUuid ? 'none' : cardUserUuid === cognitoUUID ? 'me' : 'other', [cognitoUUID])

    return <>
        <Alert
            className={styles.alert}
            icon={IconNames.Remove}
            intent={Intent.DANGER}
            isOpen={state.isRemoveMyCardDialogOpen}
            loading={isRejectingCard}
            canEscapeKeyCancel
            canOutsideClickCancel
            onCancel={() => setState(produce(draft => {
                draft.isRemoveMyCardDialogOpen = false
            }))}
            onConfirm={removeMyCardConfirmed}
            cancelButtonText={t('card.removeMyCardAlert.cancelButton.label')}
            confirmButtonText={t('card.removeMyCardAlert.confirmButton.label')}
        >
            <p>{t('card.removeMyCardAlert.message')}</p>
        </Alert>

        <Mount response={cardResponse}
            loading={() => <CardSkeleton />}
            failure={error => <ApiError error={error} />}
            success={card => <>
                {!auth.isAuthenticated && linkedUser(card.cognito_uuid) === 'none' &&
                    <Dialog
                        className={styles.dialog}
                        title={t('dialog.signInRequiredToAddToMyCard.title')}
                        icon={IconNames.Lock}
                        isOpen={state.isSignInDialogOpen}
                        onClose={() => setState(produce(draft => {
                            draft.isSignInDialogOpen = false
                        }))}>
                        <DialogBody>
                            <p>{t('dialog.signInRequiredToAddToMyCard.message')}</p>
                            <ButtonGroup
                                className={styles.buttonGroup}
                                vertical>
                                <Button
                                    text={t('dialog.signInRequiredToAddToMyCard.signInButton.label')}
                                    icon={IconNames.LogIn}
                                    intent={Intent.PRIMARY}
                                    onClick={() => navigate(signInLink(card.id))} />
                                <Button
                                    text={t('dialog.signInRequiredToAddToMyCard.signUpButton.label')}
                                    icon={IconNames.NewPerson}
                                    intent={Intent.SUCCESS}
                                    onClick={() => navigate(signUpLink(card.id))} />
                            </ButtonGroup>
                        </DialogBody>
                    </Dialog>}

                {linkedUser(card.cognito_uuid) === 'none' && <>
                    <Empty
                        lottie="verify"
                        title={t('card.addToMyCard.label')}
                        description={t('card.addToMyCard.description')}
                        buttonProps={{
                            icon: IconNames.Endorsed,
                            text: t('card.addToMyCard.button.label'),
                            onClick: addToMyCard,
                            disabled: isInsertingCard,
                        }} />
                </>}

                <Divider className={styles.spacer} />

                {(linkedUser(card.cognito_uuid) === 'me' || linkedUser(card.cognito_uuid) === 'other') &&
                    <CardDetail
                        signInLink={signInLink}
                        cardResponse={cardResponse} />}

                {linkedUser(card.cognito_uuid) === 'me' &&
                    <Button
                        className={styles.removeMyCardButton}
                        text={t('card.removeMyCardButton.label')}
                        icon={IconNames.Cross}
                        intent={Intent.DANGER}
                        onClick={() => setState(produce(draft => {
                            draft.isRemoveMyCardDialogOpen = true
                        }))}
                        minimal />}
            </>}
        />
    </>
}